import { Component, EventEmitter, Input, Output } from '@angular/core'
import { Deliverable, DeliveryStatus } from '@app/features/scoping/models/deliverable.model'
import { ScopeElementsConfig, UpdateFeeItemPayload, UpdateRolePayload, UpdateTpcPayload } from '../models'
import { formatMonetaryValue, trackById } from '@shared/utils/utils'

import { ApplicationSetting } from '@app/core/model/enums/application-setting.enum'
import { ApprovalFlowService } from '@app/features/scope-overview/service/approval-flow.service'
import { AuthService } from '@app/core/service/auth.service'
import { ComplexitiesList } from '@app/core/model/enums/complexity.enum'
import { FormControl } from '@angular/forms'
import { LanguageService } from '@app/core/service/language.service'
import { MatDialog } from '@angular/material/dialog'
import { MenuOptions } from '@core/model/definitions/menu-options.interface'
import { Money } from '@app/features/scope-overview/model/money.model'
import { Privilege } from '@app/core/model/enums/privilege.enum'
import { ScopeComponent } from '@app/features/scoping/models/component.model'
import { ScopeElementsService } from '../services/scope-elements.service'
import { ScopeUiModalComponent } from '@app/shared/components/ui-components/scope-ui-modal/scope-ui-modal.component'
import { ThirdPartyCost } from '@app/core/model/third-party-cost.model'
import { User } from '@app/core/model/user.model'
import { getThirdPartyCostColumns } from '../utils'
import { isNil } from 'lodash'
import { isPreferenceActive } from '@app/shared/components/ui-components/scope-ui-table/table-column-preferences.type'
import { plainToInstance } from 'class-transformer'
import { DescriptionUpdate } from '../../description-editor/description-editor.component'

@Component({
  selector: 'scope-components-row',
  templateUrl: './components-row.component.html',
  styleUrls: ['./components-row.component.scss'],
})
export class ComponentsRowComponent {
  @Input() component: ScopeComponent
  @Input() config: ScopeElementsConfig
  @Input() deliverable: Deliverable
  @Input() componentMenuOptions: MenuOptions[]
  @Input() feeMenuOptions: MenuOptions[]
  @Input() level: number

  @Output() onUpdateComponent = new EventEmitter<ScopeComponent>()
  @Output() onOverrideQuantity = new EventEmitter<ScopeComponent>()
  @Output() onOverrideRateCardRoleHours = new EventEmitter<UpdateRolePayload>()
  @Output() onUpdateComponentFee = new EventEmitter<UpdateFeeItemPayload>()
  @Output() onUpdateRole = new EventEmitter<UpdateRolePayload>()
  @Output() onUpdateComponentTpc = new EventEmitter<UpdateTpcPayload>()
  @Output() onReorderComponentTpcs = new EventEmitter<{
    component: ScopeComponent
    thirdPartyCosts: ThirdPartyCost[]
  }>()

  get currentUser(): User {
    return this.authService.loggedInUser
  }

  get isAgencyPriceEditable(): boolean {
    return this.config?.isEditable && this.component.fixedPricing && this.component.fixedFeeEditable
  }

  get updatedConfig(): ScopeElementsConfig {
    return { ...this.config, isEditable: this.config?.isDeliverableEditable }
  }

  isEditAgencyPrice = false
  agencyPriceControl = new FormControl()

  constructor(
    protected authService: AuthService,
    protected scopeElementsService: ScopeElementsService,
    private dialog: MatDialog,
    private lang: LanguageService,
    private scopeApprovalFlowService: ApprovalFlowService
  ) {
    this.scopeElementsService.setFeeColumns()
  }

  getTpcColumns = () =>
    getThirdPartyCostColumns(this.config?.isEditable, (thirdPartyCost: ThirdPartyCost) =>
      this.onUpdateComponentTpc.emit({ deliverable: this.deliverable, component: this.component, thirdPartyCost })
    )

  updateComponentTpc(thirdPartyCost: ThirdPartyCost) {
    this.onUpdateComponentTpc.emit({ deliverable: this.deliverable, component: this.component, thirdPartyCost })
  }

  reorderComponentTpcs(thirdPartyCosts: ThirdPartyCost[]) {
    this.onReorderComponentTpcs.emit({ component: this.component, thirdPartyCosts })
  }

  selectComponent() {
    this.scopeElementsService.toggleComponent(this.component.id)
  }

  updateComponent(updatedFields: Partial<ScopeComponent>) {
    this.onUpdateComponent.emit(plainToInstance(ScopeComponent, { ...this.component, ...updatedFields }))
  }

  saveComponentExtraDescription($event: DescriptionUpdate) {
    this.updateComponent({ description: $event.main, extraDescription: $event.extra })
  }

  getComponentPrice() {
    return this.component.fixedPricing && this.component.fixedCost
      ? new Money(
          !isNil(this.component.quantity)
            ? this.component.fixedCost.amount * this.component.quantity
            : this.component.fixedCost.amount,
          this.config?.currency
        )
          .multiply(this.component.quantityModifier)
          .multiply(this.component.priceModifier)
      : this.component.getTotalSellingPrice()
  }

  updateAgencyPrice(amount: number) {
    this.isEditAgencyPrice = false

    let fixedCost = !isNil(amount) ? new Money(amount, this.config?.currency) : null

    if (fixedCost && this.component.quantity > 1) {
      const eachValue = fixedCost.divide(this.component.quantity)
      const allValue = fixedCost.multiply(this.component.quantity)

      const dialog = this.dialog.open(ScopeUiModalComponent, {
        data: {
          title: `Fixed Fee for all or each?`,
          body: `The quantity is more than one for ${
            this.component.name
          }. Is the fixed fee you are setting for ALL ${this.lang.get('component.p')} or EACH ${this.lang.get(
            'component'
          )}`,
          confirmText: `Each - Set total at ${formatMonetaryValue(allValue)}  (${formatMonetaryValue(fixedCost)} each)`,
          confirmCallback: () => {
            this.updateComponent({ fixedCost })
            dialog.close()
          },
          hasCancelButton: true,
          cancelText: `All - Set total at ${formatMonetaryValue(fixedCost)} (${formatMonetaryValue(eachValue)} each)`,
          cancelCallback: () => {
            fixedCost = eachValue
            this.updateComponent({ fixedCost })
            dialog.close()
          },
          flatButton: true,
        },
      })
    } else {
      this.updateComponent({ fixedCost })
    }
  }

  overrideQuantity() {
    return () => this.onOverrideQuantity.emit(this.component)
  }

  editAgencyPrice($event: MouseEvent) {
    this.preventDefault($event)
    this.agencyPriceControl.setValue(this.component.fixedCost?.amount)
    this.isEditAgencyPrice = true
  }

  preventDefault(event: MouseEvent) {
    event.preventDefault()
    event.stopPropagation()
  }

  showToggleMenu() {
    if (this.scopeApprovalFlowService.isScopeEditable(this.config?.scopeVersion)) return true

    if (
      this.deliverable.isPendingTrade() &&
      this.config?.scopeVersion.findDeliverableById(this.deliverable.tradedFrom.id).tradesCount > 1
    )
      return true

    if (
      this.deliverable.deliveryStatus == DeliveryStatus.IN_PROGRESS &&
      this.config?.scopeVersion.isStateApproved() &&
      this.scopeApprovalFlowService.stateChecks.deliverableStatus.isApprover &&
      this.scopeApprovalFlowService.stateChecks.deliverableStatus.isStateApproved
    )
      return (
        this.currentUser.hasPrivilege(Privilege.SCOPE_DELIVERABLE__COMPLETE) ||
        (this.currentUser.company.hasApplicationSetting(ApplicationSetting.SCOPE__DELIVERABLE__TRADING) &&
          this.currentUser.hasPrivilege(Privilege.SCOPE_DELIVERABLE__TRADE))
      )

    if (this.deliverable.isBeingTraded())
      return (
        this.currentUser.company.hasApplicationSetting(ApplicationSetting.SCOPE__DELIVERABLE__TRADING) &&
        this.currentUser.hasPrivilege(Privilege.SCOPE_DELIVERABLE__TRADE)
      )

    if (
      this.deliverable.deliveryStatus == DeliveryStatus.ON_HOLD &&
      this.scopeApprovalFlowService.stateChecks.deliverableStatus.isStateApproved &&
      this.scopeApprovalFlowService.stateChecks.deliverableStatus.isApprover
    )
      return this.currentUser.hasPrivilege(Privilege.SCOPE_DELIVERABLE__STOP)

    return false
  }

  getRiskIndicatorClass() {
    if (!this.config?.showRiskIndicator) return 'hidden'

    switch (this.component.prediction?.riskIndicator) {
      case 'LOW':
        return 'green'
      case 'MEDIUM':
        return 'yellow'
      case 'HIGH':
        return 'red'
      case 'AGENCY_DEFINED': {
        if (this.component.sourceType == 'LIBRARY') return 'black'
        if (this.component.sourceType == 'SCOPE') return 'blue'
      }
    }

    return ''
  }

  protected readonly ComplexityOptions = ComplexitiesList
  protected readonly Privilege = Privilege
  protected readonly ApplicationSetting = ApplicationSetting
  protected readonly trackById = trackById
  protected readonly isPreferenceActive = isPreferenceActive
}
